"use client";
import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { motion } from "framer-motion";
import { FaComments, FaLaptopCode, FaRocket } from "react-icons/fa";

const howWeWorkData = [
  {
    id: 1,
    title: "Talk to Us",
    description: "We start by having a friendly chat to understand your needs and ideas for your website.",
    icon: <FaComments className="w-6 h-6" />,
    visual: "form-mobile",
  },
  {
    id: 2,
    title: "Build Your Website",
    description: "Our team designs and builds a beautiful, easy-to-use website just for you.",
    icon: <FaLaptopCode className="w-6 h-6" />,
    visual: "timeline",
  },
  {
    id: 3,
    title: "Go Live & Support",
    description: "Your website goes live, and we continue to help you with any updates or support you need.",
    icon: <FaRocket className="w-6 h-6" />,
    visual: "notifications",
  },
];

const HowWeWorkSteps = () => {
  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <section 
      id="HowWeWorkSteps" 
      className="relative overflow-hidden bg-gradient-to-b from-primary/10 to-primary/5 dark:from-dark-background dark:to-dark-surface px-4 py-16 sm:py-24 lg:px-24"
    >
      {/* Floating Blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute w-[600px] h-[600px] -top-48 -left-48 bg-gradient-to-r from-secondary-400/20 to-secondary-300/30 dark:from-dark-surface/20 dark:to-dark-background/20 rounded-full blur-3xl opacity-30 dark:opacity-15 animate-float" />
        <div className="absolute w-[400px] h-[400px] -bottom-32 -right-32 bg-gradient-to-r from-secondary-300/20 to-secondary-400/30 dark:from-dark-background/20 dark:to-dark-surface/20 rounded-full blur-3xl opacity-30 dark:opacity-15 animate-float-delayed" />
      </div>

      {/* Background Gradients & Patterns */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute left-[calc(50%-11rem)] sm:left-[calc(50%-30rem)] aspect-[1155/678] w-[82.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-primary/30 to-primary/10 opacity-20 dark:opacity-5"
          style={{
            clipPath: "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-10 dark:opacity-5"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='52' height='26' viewBox='0 0 52 26' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='0.1'%3E%3Cpath d='M10 10c0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4 3.314 0 6 2.686 6 6 0 2.21 1.79 4 4 4v2c-3.314 0-6-2.686-6-6 0-2.21-1.79-4-4-4-3.314 0-6-2.686-6-6zm25.464-1.95l8.486 8.486-1.414 1.414-8.486-8.486 1.414-1.414z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-20" data-aos="fade-up">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600"
          >
            HOW WE WORK
          </motion.h2>
          <p className="text-base sm:text-lg md:text-xl text-light-text-secondary dark:text-dark-text-secondary max-w-2xl mx-auto font-light px-4">
            Our simple process to build your website and keep it running smoothly.
          </p>
        </div>

        {/* Process Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {howWeWorkData.map((step, index) => (
            <motion.div
              key={step.id}
              whileHover={{ y: -8 }}
              className="relative bg-light-surface dark:bg-dark-surface/90 backdrop-blur-lg rounded-2xl p-6 sm:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-light-border dark:border-dark-border overflow-hidden"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              {/* Animated Gradient Border */}
              <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 opacity-0 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 animate-flowing-border" />
              </div>

              {/* Header */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-light-surface text-xl shadow-lg">
                  {step.icon}
                </div>
                <span className="text-sm font-medium text-primary-500 dark:text-dark-text-secondary">
                  Step 0{index + 1}
                </span>
              </div>

              {/* Content */}
              <div className="space-y-4">
                <h3 className="text-xl sm:text-2xl font-semibold text-light-text-primary dark:text-dark-text-primary">
                  {step.title}
                </h3>
                <p className="text-sm sm:text-base text-light-text-secondary dark:text-dark-text-secondary font-light leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Visual Decorations */}
              {step.visual === "form-mobile" && (
                <div className="mt-6 relative h-40 bg-primary/20 dark:bg-dark-surface/20 rounded-xl overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary-600/10" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-40 bg-dark-surface rounded-lg shadow-2xl transform rotate-1">
                      <div className="p-2 space-y-2">
                        {[1, 2, 3].map((line) => (
                          <div
                            key={line}
                            className="h-2 bg-primary/30 dark:bg-dark-border/30 rounded-full animate-pulse-slow"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {step.visual === "timeline" && (
                <div className="mt-6 h-40 relative flex items-center justify-center">
                  <div className="absolute w-full h-1 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full" />
                  <div className="flex justify-between absolute w-full">
                    {[1, 2, 3].map((point) => (
                      <div
                        key={point}
                        className="w-6 h-6 bg-light-surface dark:bg-dark-surface border-2 border-primary-500 dark:border-dark-border rounded-full flex items-center justify-center text-xs font-bold text-primary-500 dark:text-dark-text-secondary"
                      >
                        ✓
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step.visual === "notifications" && (
                <div className="mt-6 relative h-40 flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-primary-600/10 rounded-xl" />
                  <div className="relative space-y-4">
                    <div className="w-48 p-3 bg-light-surface dark:bg-dark-surface rounded-lg shadow-lg transform rotate-2">
                      <div className="flex gap-2">
                        <div className="w-8 h-8 bg-primary-500 dark:bg-dark-border rounded-lg" />
                        <div className="flex-1 space-y-1">
                          <div className="h-2 bg-light-border dark:bg-dark-border rounded-full w-3/4" />
                          <div className="h-2 bg-light-border dark:bg-dark-border rounded-full w-1/2" />
                        </div>
                      </div>
                    </div>
                    <div className="w-48 p-3 bg-light-surface dark:bg-dark-surface rounded-lg shadow-lg transform -rotate-1 ml-auto">
                      <div className="h-2 bg-light-border dark:bg-dark-border rounded-full mb-2 w-2/3" />
                      <div className="h-8 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-dark-border dark:to-dark-border/70 rounded-lg" />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Flowing Border Animation CSS */}
      <style jsx>{`
        @keyframes flowing-border {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-flowing-border {
          animation: flowing-border 2s linear infinite;
          background: linear-gradient(90deg, transparent, theme('colors.primary.500'), transparent);
          width: 100%;
          height: 2px;
        }
      `}</style>
    </section>
  );
};

export default HowWeWorkSteps;